import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Avatar } from '../components/Avatar';
import { Skeleton } from '../components/Skeleton';
import { useToast } from '../components/Toast';
import { useSession, signOut } from '../lib/session';
import { supabase } from '../lib/supabase';
import {
  updateOwnProfile,
  uploadProfileImage,
  type ProfileEditPatch,
} from '../lib/queries';
import { CardBook } from '../components/CardBook';
import { AddressBook } from '../components/AddressBook';
import { AvatarCropper } from '../components/AvatarCropper';
import { ProfileCard } from '../components/ProfileCard';
import { ProfileLikes } from '../components/ProfileLikes';
import PasswordField from '../components/PasswordField';
import { withTimeout } from '../lib/timeout';
import { checkHandleAvailable } from '../lib/onboarding-queries';
import {
  SOCIAL_PLATFORMS,
  type SocialPlatformId,
} from '../lib/socials';
import { CountrySelect } from '../components/CountrySelect';
import { Button, Field, Input, Textarea, Text, controlClass, Tabs, type TabItem } from '../components/ui';
import './Profile.css';

/* Size a textarea to its content so the Bio / treasured-garment boxes always
   show the whole text - runs once on mount (ref) and again on every input. */
function autoGrowTextarea(el: HTMLTextAreaElement | null) {
  if (!el) return;
  el.style.height = 'auto';
  el.style.height = `${el.scrollHeight + 2}px`;
}

type ProfileTab = 'preview' | 'profile' | 'social' | 'billing' | 'addresses' | 'security' | 'likes';

const PROFILE_TABS: TabItem<ProfileTab>[] = [
  { value: 'preview', label: 'Preview' },
  { value: 'profile', label: 'Profile' },
  { value: 'social', label: 'Social' },
  { value: 'billing', label: 'Billing' },
  { value: 'addresses', label: 'Addresses' },
  { value: 'security', label: 'Security' },
  { value: 'likes', label: 'Likes' },
];

export default function Profile() {
  const { profile, loading } = useSession();
  const [tab, setTab] = useState<ProfileTab>('preview');

  if (loading) {
    return (
      <div className="profile profile--me" aria-busy="true" aria-label="Loading profile">
        <aside className="profile__stripe" aria-hidden="true">
          <ProfileStripeLogo />
        </aside>
        <main className="profile__pane container">
          <div className="profile__pane-inner">
            <div className="profile__identity">
              <Skeleton variant="avatar" width={112} height={112} />
              <div style={{ height: 'var(--space-4)' }} />
              <Skeleton variant="text" width={220} height={28} />
              <div style={{ height: 'var(--space-2)' }} />
              <Skeleton variant="text" width="40%" />
            </div>
          </div>
        </main>
      </div>
    );
  }
  if (!profile) return null;

  return (
    <div className="profile profile--me">
      <aside className="profile__stripe" aria-hidden="true">
        <ProfileStripeLogo />
      </aside>

      <main className="profile__pane container">
        <div className="profile__pane-inner">
          <p className="kicker profile__eyebrow">Your profile</p>

          <Tabs
            items={PROFILE_TABS}
            value={tab}
            onChange={setTab}
            aria-label="Profile sections"
            className="profile__tabs"
          />

          {tab === 'preview' && (
            <div className="profile__tabpanel">
              <p className="profile__preview-note">
                This is how members see you{profile.handle ? <> at <strong>/u/{profile.handle}</strong></> : null}.
              </p>
              <ProfileCard profile={profile} />
            </div>
          )}

          {(tab === 'profile' || tab === 'social') && (
            <ProfileEditor activeSection={tab} />
          )}

          {tab === 'billing' && (
            <div className="profile__tabpanel">
              <BillingSection userId={profile.id} />
              <hr className="profile__rule" />
              <CardBook />
            </div>
          )}

          {tab === 'addresses' && (
            <div className="profile__tabpanel">
              <AddressBook />
            </div>
          )}

          {tab === 'security' && (
            <div className="profile__tabpanel">
              <SecurityPanel />
            </div>
          )}

          {tab === 'likes' && (
            <div className="profile__tabpanel">
              <ProfileLikes userId={profile.id} />
            </div>
          )}

          {/* Accent-bar pointer - same hint members get on onboarding step 1, so
              they can rediscover the app theming. Only on the Profile tab. */}
          {tab === 'profile' && (
            <p className="profile__accent-note">
              <span className="profile__accent-arrow" aria-hidden="true">↓</span>
              <span>
                Make it yours - tap any swatch on the colour bar below to theme the
                whole app to your taste. Swipe the bar sideways on a phone to see
                every colour.
              </span>
            </p>
          )}

          <hr className="profile__rule" />

          <footer className="profile__signout">
            <Button type="button" variant="soft" size="sm" onClick={signOut}>
              Sign out
            </Button>
          </footer>
        </div>
      </main>
    </div>
  );
}

export function ProfileStripeLogo() {
  return (
    <div className="profile__logo">
      <img className="profile__logo-ring" src="/logos/cc-logo-icon-white.png" alt="" />
      <span className="profile__logo-wordmark">
        <img src="/logos/cc-logo-full-white.png" alt="" />
      </span>
    </div>
  );
}

interface SubRow {
  status: 'none' | 'trialing' | 'active' | 'past_due' | 'canceled' | 'comped';
  plan: string | null;
  tier: string | null;
  current_period_end: string | null;
  cancel_at_period_end: boolean;
  stripe_customer_id: string | null;
}

function BillingSection({ userId }: { userId: string }) {
  const toast = useToast();
  const [sub, setSub] = useState<SubRow | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [openingPortal, setOpeningPortal] = useState<null | 'manage' | 'switch_annual' | 'cancel'>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data, error } = await supabase
        .from('subscriptions')
        .select('status, plan, tier, current_period_end, cancel_at_period_end, stripe_customer_id')
        .eq('user_id', userId)
        .maybeSingle();
      if (cancelled) return;
      if (error) {
        console.warn('[profile/billing] subscription fetch failed', error);
        setLoaded(true);
        return;
      }
      setSub(data as SubRow | null);
      setLoaded(true);
    })();
    return () => { cancelled = true; };
  }, [userId]);

  if (!loaded) {
    return (
      <section className="profile__section">
        <p className="kicker">Billing</p>
        <Skeleton variant="text" width="60%" />
      </section>
    );
  }

  if (sub?.status === 'comped') {
    return (
      <section className="profile__section">
        <p className="kicker">Billing</p>
        <p className="profile__section-body">
          Complimentary Access
        </p>
      </section>
    );
  }

  if (!sub || sub.status === 'none' || (sub.status === 'canceled' && !sub.stripe_customer_id)) {
    return (
      <section className="profile__section">
        <p className="kicker">Billing</p>
        <p className="profile__section-body">
          You don't have an active membership.
        </p>
        <Button as={Link} to="/subscribe" variant="accent" size="sm">
          Join the Club
        </Button>
      </section>
    );
  }

  const planLabel = sub.plan === 'annual' ? 'Annual' : sub.plan === 'monthly' ? 'Monthly' : (sub.plan ?? ' - ');
  const renewalDate = sub.current_period_end
    ? new Date(sub.current_period_end).toLocaleDateString(undefined, { dateStyle: 'medium' })
    : null;

  async function openPortal(flow: 'manage' | 'switch_annual' | 'cancel') {
    setOpeningPortal(flow);
    try {
      const { data: sess } = await supabase.auth.getSession();
      const token = sess.session?.access_token;
      if (!token) throw new Error('Sign in first.');
      const flowParam =
        flow === 'switch_annual' ? 'subscription_update' :
        flow === 'cancel'        ? 'subscription_cancel' :
                                    undefined;
      const res = await fetch('/api/stripe/portal', {
        method: 'POST',
        headers: {
          'content-type': 'application/json',
          authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(flowParam ? { flow: flowParam } : {}),
      });
      const data = await res.json();
      if (!res.ok || !data?.url) throw new Error(data?.error ?? 'Could not open billing portal.');
      window.location.assign(data.url);
    } catch (err: any) {
      toast.error(err?.message ?? 'Portal failed.');
      setOpeningPortal(null);
    }
  }

  if (sub.status === 'past_due') {
    return (
      <section className="profile__section">
        <p className="kicker" style={{ color: 'var(--accent)' }}>Payment failed</p>
        <p className="profile__section-body">
          Your last payment didn't go through. Update your card to keep access.
        </p>
        <Button
          type="button"
          variant="primary"
          size="sm"
          onClick={() => openPortal('manage')}
          disabled={openingPortal !== null}
        >
          {openingPortal === 'manage' ? '…' : 'Update payment method'}
        </Button>
      </section>
    );
  }

  return (
    <section className="profile__section">
      <p className="kicker">Billing</p>
      <p className="profile__section-body">
        Conversion Club - {planLabel}
        {sub.cancel_at_period_end && renewalDate && (
          <> · ending {renewalDate}</>
        )}
        {!sub.cancel_at_period_end && renewalDate && (
          <> · renews {renewalDate}</>
        )}
      </p>
      <div className="profile__section-actions">
        <Button
          type="button"
          variant="soft"
          size="sm"
          onClick={() => openPortal('manage')}
          disabled={openingPortal !== null}
        >
          {openingPortal === 'manage' ? '…' : 'Manage billing'}
        </Button>
        {sub.plan === 'monthly' && !sub.cancel_at_period_end && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => openPortal('switch_annual')}
            disabled={openingPortal !== null}
            title="Switch to annual billing"
          >
            {openingPortal === 'switch_annual' ? '…' : 'Switch to annual'}
          </Button>
        )}
      </div>
    </section>
  );
}

function ProfileEditor({ activeSection }: { activeSection: 'profile' | 'social' }) {
  const { profile } = useSession();
  const toast = useToast();
  const [displayName, setDisplayName] = useState(profile?.display_name ?? '');
  const [handle, setHandle] = useState(profile?.handle ?? '');
  const [location, setLocation] = useState(profile?.location ?? '');
  const [country, setCountry] = useState(profile?.country ?? '');
  const [bio, setBio] = useState(profile?.bio ?? '');
  const [fabric, setFabric] = useState(profile?.fabric_selection ?? '');
  // Socials are stored as a {platform_id: handle} map containing ONLY the
  // platforms the user has chosen to add. Adding a platform inserts an
  // empty-string entry; removing drops the entry entirely. This drives
  // both the rendered input rows and the contents of the "Add platform"
  // dropdown (which lists platforms not currently in the map).
  const [socials, setSocials] = useState<Partial<Record<SocialPlatformId, string>>>(() => {
    const out: Partial<Record<SocialPlatformId, string>> = {};
    const stored = profile?.social_links ?? {};
    for (const p of SOCIAL_PLATFORMS) {
      const v = stored[p.id];
      if (typeof v === 'string' && v.trim()) out[p.id] = v;
    }
    // Pull the legacy column in if social_links doesn't already carry IG.
    if (!out.instagram && profile?.instagram_handle) {
      out.instagram = profile.instagram_handle.replace(/^@+/, '');
    }
    return out;
  });
  const addedPlatforms = SOCIAL_PLATFORMS.filter((p) => p.id in socials);
  const availablePlatforms = SOCIAL_PLATFORMS.filter((p) => !(p.id in socials));
  const [avatarUrl, setAvatarUrl] = useState(profile?.avatar_url ?? '');
  const [avatarBusy, setAvatarBusy] = useState(false);
  // The picked file waits here while the member crops/positions it (same
  // round-viewport cropper as onboarding); only the cropped square is uploaded.
  const [cropFile, setCropFile] = useState<File | null>(null);
  const avatarInput = useRef<HTMLInputElement>(null);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  if (!profile) return null;

  async function handleUpload(file: File | null) {
    const p = profile;
    if (!file || !p) return;
    setAvatarBusy(true);
    try {
      const url = await uploadProfileImage({ userId: p.id, kind: 'avatar', file });
      setAvatarUrl(url);
      toast.success('Avatar uploaded.');
    } catch (err: any) {
      console.error('[profile] upload failed', err);
      toast.error(err?.message ?? 'Upload failed');
    } finally {
      setAvatarBusy(false);
    }
  }

  async function saveProfile(e: React.FormEvent) {
    e.preventDefault();
    setSaving(true);
    setMsg(null);
    // Sanitize each social value through its platform's rule, drop blanks,
    // and emit a clean Record. IG also writes through to the legacy
    // instagram_handle column so older surfaces keep working until they're
    // migrated over.
    const cleanSocials: Record<string, string> = {};
    for (const platform of SOCIAL_PLATFORMS) {
      const raw = (socials[platform.id] ?? '').trim();
      if (!raw) continue;
      const clean = platform.sanitize(raw);
      if (clean) cleanSocials[platform.id] = clean;
    }
    const patch: ProfileEditPatch = {
      display_name: displayName.trim(),
      handle: handle.trim() || null,
      location: location.trim() || null,
      country: country || null,
      bio: bio.trim() || null,
      fabric_selection: fabric.trim() || null,
      instagram_handle: cleanSocials.instagram ?? null,
      social_links: cleanSocials,
      avatar_url: avatarUrl || null,
    };
    try {
      // Handles are unique club-wide. Verify availability BEFORE writing so a
      // clash reads as a clear message, not a raw constraint error. The check
      // treats the user's own current handle as available.
      if (patch.handle) {
        const free = await withTimeout(
          checkHandleAvailable(patch.handle),
          10000,
          'Handle check',
        );
        if (!free) {
          const taken = 'That @handle is already taken - try another.';
          setMsg(taken);
          toast.error(taken);
          setSaving(false);
          return;
        }
      }
      await withTimeout(updateOwnProfile(profile!.id, patch), 15000, 'Save');
      toast.success('Profile saved.');
      setSaving(false);
      // Full reload so SessionProvider re-fetches the profile + every
      // surface (nav avatar, comment author cards, banner) picks the new
      // values up at once.
      setTimeout(() => window.location.reload(), 600);
      return;
    } catch (err: any) {
      console.error('[profile] save failed', err);
      const m = err?.message ?? '';
      // The unique-violation backstop catches the race where someone claims
      // the handle between our availability check and the write.
      const friendly = /handle_taken|duplicate key|profiles_handle_key/i.test(m)
        ? 'That @handle is already taken - try another.'
        : /invalid_handle/.test(m)
          ? 'Handle must be 2–30 chars: a–z, 0–9, underscore.'
          : m || 'Save failed';
      setMsg(friendly);
      toast.error(friendly);
    }
    setSaving(false);
  }

  return (
    <section className="profile__edit">
      {cropFile && (
        <AvatarCropper
          file={cropFile}
          onCancel={() => setCropFile(null)}
          onConfirm={(cropped) => {
            setCropFile(null);
            void handleUpload(cropped);
          }}
        />
      )}
      <form className="profile__form" onSubmit={saveProfile}>
        {activeSection === 'profile' && (
        <>
        {/* Identity-first: avatar + name + handle lead the form (matches the read
            view's lockup) so editing always reads as "this is me". */}
        <section className="profile__card profile__identity-edit">
          <div className="profile__avatar-edit-row">
            <span
              className={
                'profile__avatar-edit-circle' +
                (avatarBusy ? ' profile__avatar-edit-circle--busy' : '')
              }
            >
              {avatarUrl ? (
                <img src={avatarUrl} alt="" />
              ) : (
                <Avatar
                  name={profile.display_name || 'You'}
                  size="lg"
                  founding={profile.is_founding_member ?? false}
                />
              )}
            </span>
            <div className="profile__avatar-edit-actions">
              <input
                ref={avatarInput}
                type="file"
                accept="image/*"
                className="visually-hidden"
                onChange={(e) => {
                  const f = e.target.files?.[0] ?? null;
                  if (f) setCropFile(f);
                  e.currentTarget.value = '';
                }}
              />
              <Button
                type="button"
                variant="soft"
                size="sm"
                onClick={() => avatarInput.current?.click()}
                disabled={avatarBusy}
              >
                {avatarBusy ? 'Uploading…' : avatarUrl ? 'Replace' : 'Upload'}
              </Button>
              <Text role="caption" muted className="profile__form-hint">Square JPG or PNG.</Text>
            </div>
          </div>

          <div className="profile__form-row">
            <Field label="Name" htmlFor="pf-name">
              <Input
                id="pf-name"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                maxLength={80}
                autoComplete="name"
                placeholder="Your name"
              />
            </Field>
            <Field label="Handle" htmlFor="pf-handle">
              <div className="profile__handle-wrap">
                <span className="profile__handle-at" aria-hidden="true">@</span>
                <Input
                  id="pf-handle"
                  value={handle}
                  onChange={(e) => setHandle(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, ''))}
                  maxLength={30}
                  placeholder="handle"
                  autoCapitalize="none"
                  autoCorrect="off"
                  spellCheck={false}
                />
              </div>
            </Field>
          </div>
        </section>

        <section className="profile__card">
          <Field label="Location" htmlFor="pf-city">
            <div className="profile__loc-row">
              <CountrySelect value={country} onChange={setCountry} className="profile__select" compact />
              <Input
                id="pf-city"
                value={location}
                onChange={(e) => setLocation(e.target.value)}
                maxLength={80}
                placeholder="City / area"
                autoComplete="address-level2"
                aria-label="City or area"
              />
            </div>
          </Field>

          <Field label="Bio" htmlFor="pf-bio">
            <Textarea
              id="pf-bio"
              ref={autoGrowTextarea}
              value={bio}
              onChange={(e) => setBio(e.target.value)}
              onInput={(e) => autoGrowTextarea(e.currentTarget)}
              maxLength={500}
              rows={3}
              placeholder="Tell us about yourself and why you're here!"
            />
          </Field>

          <Field label="Most treasured garment" htmlFor="pf-fabric">
            <Textarea
              id="pf-fabric"
              ref={autoGrowTextarea}
              value={fabric}
              onChange={(e) => setFabric(e.target.value)}
              onInput={(e) => autoGrowTextarea(e.currentTarget)}
              maxLength={300}
              rows={2}
              placeholder="Tell us all about your garment!"
            />
          </Field>
        </section>
        </>
        )}

        {activeSection === 'social' && (
        <fieldset className="profile__card profile__socials-edit">
          {/* The visible title lives OUTSIDE the legend: rendered legends sit
              outside the fieldset's flex flow, so they ignore the card gap and
              break the uniform title→content rhythm. */}
          <legend className="visually-hidden">Social links</legend>
          <div className="profile__socials-head">
            <p className="kicker">Social links</p>
            <Text role="caption" muted className="profile__form-hint">
              Add the platforms you want shown on your profile. Each one
              becomes an icon people can click through.
            </Text>
          </div>

          {addedPlatforms.length > 0 && (
            <ul className="profile__social-rows">
              {addedPlatforms.map((platform) => (
                <li key={platform.id} className="profile__social-input">
                  <span className="profile__social-input-icon" aria-hidden="true">
                    <platform.Icon />
                  </span>
                  <label className="visually-hidden" htmlFor={`social-${platform.id}`}>
                    {platform.label}
                  </label>
                  <div
                    className={`profile__social-input-wrap${platform.prefix ? ' profile__social-input-wrap--at' : ''}`}
                  >
                    {platform.prefix && (
                      <span className="profile__social-at" aria-hidden="true">{platform.prefix}</span>
                    )}
                    <Input
                      id={`social-${platform.id}`}
                      value={socials[platform.id] ?? ''}
                      onChange={(e) =>
                        setSocials((s) => ({ ...s, [platform.id]: e.target.value }))
                      }
                      maxLength={200}
                      placeholder={platform.placeholder}
                      autoCapitalize="none"
                      autoCorrect="off"
                      spellCheck={false}
                    />
                  </div>
                  <button
                    type="button"
                    className="profile__social-remove"
                    onClick={() =>
                      setSocials((s) => {
                        const next = { ...s };
                        delete next[platform.id];
                        return next;
                      })
                    }
                    aria-label={`Remove ${platform.label}`}
                    title={`Remove ${platform.label}`}
                  >
                    ×
                  </button>
                </li>
              ))}
            </ul>
          )}

          {availablePlatforms.length > 0 && (
            <div className="profile__social-add">
              <label htmlFor="social-add">
                <span className="visually-hidden">Add a platform</span>
              </label>
              <select
                id="social-add"
                value=""
                onChange={(e) => {
                  const id = e.target.value as SocialPlatformId | '';
                  if (!id) return;
                  setSocials((s) => ({ ...s, [id]: '' }));
                }}
              >
                <option value="">+ Add a platform…</option>
                {availablePlatforms.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.label}
                  </option>
                ))}
              </select>
            </div>
          )}
        </fieldset>
        )}

        {msg && <p className="profile__form-msg">{msg}</p>}

        <div className="profile__form-actions">
          <Button type="submit" variant="primary" size="md" disabled={saving}>
            {saving ? 'Saving…' : 'Save changes'}
          </Button>
        </div>
      </form>
    </section>
  );
}

/** Security tab: set / change the account password. Split out of the profile
 *  editor so it lives on its own tab. */
function SecurityPanel() {
  const toast = useToast();
  const [pw1, setPw1] = useState('');
  const [pw2, setPw2] = useState('');
  const [pwMsg, setPwMsg] = useState<string | null>(null);
  const [pwBusy, setPwBusy] = useState(false);

  async function changePassword(e: React.FormEvent) {
    e.preventDefault();
    setPwMsg(null);
    if (pw1.length < 8) { setPwMsg('At least 8 characters.'); return; }
    if (pw1 !== pw2) { setPwMsg("Passwords don't match."); return; }
    setPwBusy(true);
    try {
      const { error } = await withTimeout(
        supabase.auth.updateUser({ password: pw1 }),
        15000,
        'Password update',
      );
      if (error) throw error;
      setPw1('');
      setPw2('');
      toast.success('Password updated.');
    } catch (err: any) {
      setPwMsg(err?.message ?? 'Update failed');
      toast.error(err?.message ?? 'Password update failed.');
    } finally {
      setPwBusy(false);
    }
  }

  return (
    <form className="profile__form profile__card profile__form--pw" onSubmit={changePassword}>
      <header className="profile__form-head">
        <p className="kicker">Change password</p>
        <Text role="caption" muted className="profile__form-hint">Sets a password on your account so you can sign in without a one time link.</Text>
      </header>
      <div className="profile__form-row">
        <Field label="New password" htmlFor="pf-pw1">
          <PasswordField
            id="pf-pw1"
            className={controlClass}
            value={pw1}
            onChange={(e) => setPw1(e.target.value)}
            minLength={8}
            autoComplete="new-password"
          />
        </Field>
        <Field label="Confirm" htmlFor="pf-pw2">
          <PasswordField
            id="pf-pw2"
            className={controlClass}
            value={pw2}
            onChange={(e) => setPw2(e.target.value)}
            minLength={8}
            autoComplete="new-password"
          />
        </Field>
      </div>
      {pwMsg && <p className="profile__form-msg">{pwMsg}</p>}
      <div className="profile__form-actions">
        <Button type="submit" variant="soft" size="md" disabled={pwBusy || !pw1 || !pw2}>
          {pwBusy ? 'Updating…' : 'Update password'}
        </Button>
      </div>
    </form>
  );
}

